import { CartAddEvent } from '@theme/events';

class CustomBundleAtc extends HTMLElement {
  #items = [];
  #button = null;
  #autoship = null;
  #status = null;
  #offer = null;

  connectedCallback() {
    this.#items = this.#parseItems();
    this.#button = this.querySelector('[data-bundle-atc]');
    this.#autoship = this.querySelector('[data-bundle-autoship]');
    this.#status = this.querySelector('[data-bundle-status]');
    this.#offer = this.querySelector('[data-bundle-offer]');
    this.#button?.addEventListener('click', this.#handleClick);
    this.#autoship?.addEventListener('change', this.#updateOffer);
    this.#updateOffer();
  }

  disconnectedCallback() {
    this.#button?.removeEventListener('click', this.#handleClick);
    this.#autoship?.removeEventListener('change', this.#updateOffer);
  }

  #updateOffer = () => {
    if (!this.#offer) return;

    const mode = this.#autoship?.checked ? 'subscription' : 'onetime';
    const { dataset } = this.#offer;
    const price = this.#offer.querySelector('[data-bundle-price]');
    const compare = this.#offer.querySelector('[data-bundle-compare]');
    const priceKey = mode === 'subscription' ? 'priceSubscription' : 'priceOnetime';
    const compareKey = mode === 'subscription' ? 'compareSubscription' : 'compareOnetime';

    if (price) price.textContent = dataset[priceKey];
    if (compare) compare.hidden = dataset[compareKey] !== 'true';
  };

  #parseItems() {
    try {
      return JSON.parse(this.querySelector('[data-bundle-items]')?.textContent ?? '[]');
    } catch {
      return [];
    }
  }

  #discountCode() {
    return this.#autoship?.checked
      ? this.dataset.discountCodeSubscription
      : this.dataset.discountCodeOnetime;
  }

  #buildCartItems(discountCode) {
    const withSubscription = this.#autoship?.checked ?? false;
    const bundleProperties = { [this.dataset.bundleProperty]: this.dataset.bundleTitle };
    const toCartItem = ({ id, quantity, selling_plan }) =>
      withSubscription && selling_plan ? { id, quantity, selling_plan } : { id, quantity };
    const [parent, ...children] = this.#items;

    return [
      {
        ...toCartItem(parent),
        properties: {
          ...bundleProperties,
          _bundle_parent: 'true',
          ...(discountCode && { _bundle_discount: discountCode }),
        },
      },
      ...children.map((child) => ({
        ...toCartItem(child),
        parent_id: parent.id,
        properties: bundleProperties,
      })),
    ];
  }

  #cartSectionIds() {
    return [...document.querySelectorAll('cart-items-component')]
      .map((element) => element.dataset.sectionId)
      .filter(Boolean);
  }

  #handleClick = async () => {
    if (this.#button.disabled || this.#button.getAttribute('aria-busy') === 'true') return;
    if (!this.#items.length) return;

    this.#setBusy(true);
    this.#setStatus('');

    try {
      const sections = this.#cartSectionIds();
      const discountCode = this.#discountCode();
      await this.#clearDiscounts();
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: this.#buildCartItems(discountCode),
          ...(sections.length && { sections: sections.join(',') }),
        }),
      });
      const data = await response.json();

      if (!response.ok || data.status) {
        this.#setStatus(data.description ?? data.message ?? this.dataset.errorMessage);
        return;
      }

      const cart = discountCode
        ? await this.#applyDiscount(discountCode, sections)
        : await this.#fetchCart();

      this.#notifyCart(cart, cart?.sections ?? data.sections);
      await this.#openCart();
    } catch {
      this.#setStatus(this.dataset.errorMessage);
    } finally {
      this.#setBusy(false);
    }
  };

  #fetchCart() {
    return fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .catch(() => null);
  }

  #updateDiscount(discount, sections = []) {
    return fetch(`${Theme.routes.cart_update_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        discount,
        ...(sections.length && { sections: sections.join(',') }),
      }),
    });
  }

  async #clearDiscounts() {
    const cart = await this.#fetchCart();
    if (!cart?.discount_codes?.length) return;

    await this.#updateDiscount('').catch(() => null);
  }

  async #applyDiscount(code, sections) {
    return this.#updateDiscount(code, sections)
      .then((response) => (response.ok ? response.json() : this.#fetchCart()))
      .catch(() => this.#fetchCart());
  }

  #notifyCart(cart, sections) {
    document.dispatchEvent(
      new CartAddEvent(cart ?? {}, this.id, {
        source: 'custom-bundle-atc',
        itemCount: cart?.item_count ?? 0,
        sections,
      })
    );
  }

  async #openCart() {
    const kaching = window.kachingCartApi;

    if (kaching?.openCart) {
      await kaching.refreshCart?.();
      kaching.openCart();
      return;
    }

    document.querySelector('cart-drawer-component')?.open?.();
  }

  #setBusy(isBusy) {
    this.#button.setAttribute('aria-busy', String(isBusy));
  }

  #setStatus(message) {
    if (this.#status) this.#status.textContent = message;
  }
}

if (!customElements.get('custom-bundle-atc')) {
  customElements.define('custom-bundle-atc', CustomBundleAtc);
}
